import { useEffect, useRef } from 'react';
import { toast } from 'sonner';

/**
 * Idle-logout timer. Uses org.session_timeout_minutes from the user object
 * (falls back to 60 min). Warns 60s before logout, then calls onLogout().
 * Activity that resets the timer: mousemove, keydown, click, scroll, touch.
 */
export default function IdleLogoutManager({ user, onLogout }) {
  const timerRef = useRef(null);
  const warnRef = useRef(null);
  const warnedRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    const minutes = user.session_timeout_minutes || 60;
    const timeoutMs = minutes * 60 * 1000;
    const warnMs = Math.max(timeoutMs - 60_000, 30_000); // warn 60s before

    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
      warnedRef.current = false;
      warnRef.current = setTimeout(() => {
        warnedRef.current = true;
        toast.warning('You will be signed out in 60 seconds due to inactivity.', { duration: 8000 });
      }, warnMs);
      timerRef.current = setTimeout(() => {
        toast.info('Signed out — session timed out for security.', { duration: 6000 });
        onLogout?.();
      }, timeoutMs);
    };

    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
    events.forEach(e => window.addEventListener(e, reset, { passive: true }));
    reset();

    return () => {
      events.forEach(e => window.removeEventListener(e, reset));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (warnRef.current) clearTimeout(warnRef.current);
    };
  }, [user, onLogout]);

  return null;
}
